import { useRef, useState, useEffect, useCallback } from 'react';
import SignaturePadLib from 'signature_pad';

export interface SignatureData {
  type: 'typed' | 'drawn';
  data: string;
}

interface SignaturePadProps {
  onSignatureChange: (data: SignatureData | null) => void;
}

export function SignaturePad({ onSignatureChange }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePadLib | null>(null);
  const [mode, setMode] = useState<'typed' | 'drawn'>('drawn');
  const [typedSignature, setTypedSignature] = useState('');
  const [hasDrawnSignature, setHasDrawnSignature] = useState(false);

  // Initialize signature pad when mode changes to 'drawn'
  useEffect(() => {
    if (canvasRef.current && mode === 'drawn') {
      signaturePadRef.current = new SignaturePadLib(canvasRef.current, {
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: 'rgb(0, 0, 0)',
      });

      signaturePadRef.current.addEventListener('endStroke', () => {
        const isEmpty = signaturePadRef.current?.isEmpty() ?? true;
        setHasDrawnSignature(!isEmpty);
        if (!isEmpty) {
          onSignatureChange({
            type: 'drawn',
            data: signaturePadRef.current?.toDataURL('image/png') || '',
          });
        }
      });

      // Resize canvas to fit container
      const resizeCanvas = () => {
        if (canvasRef.current && signaturePadRef.current) {
          const canvas = canvasRef.current;
          const ratio = Math.max(window.devicePixelRatio || 1, 1);
          canvas.width = canvas.offsetWidth * ratio;
          canvas.height = canvas.offsetHeight * ratio;
          canvas.getContext('2d')?.scale(ratio, ratio);
          signaturePadRef.current.clear();
          setHasDrawnSignature(false);
          onSignatureChange(null);
        }
      };

      resizeCanvas();
      window.addEventListener('resize', resizeCanvas);

      return () => {
        window.removeEventListener('resize', resizeCanvas);
        signaturePadRef.current?.off();
      };
    }
  }, [mode, onSignatureChange]);

  // Update typed signature callback
  const handleTypedChange = useCallback(
    (value: string) => {
      setTypedSignature(value);
      if (mode === 'typed') {
        if (value.trim()) {
          onSignatureChange({ type: 'typed', data: value });
        } else {
          onSignatureChange(null);
        }
      }
    },
    [mode, onSignatureChange]
  );

  // Update signature when mode changes
  useEffect(() => {
    if (mode === 'typed' && typedSignature.trim()) {
      onSignatureChange({ type: 'typed', data: typedSignature });
    } else if (mode === 'typed') {
      onSignatureChange(null);
    }
  }, [mode, typedSignature, onSignatureChange]);


  const clearSignature = () => {
    if (mode === 'drawn') {
      signaturePadRef.current?.clear();
      setHasDrawnSignature(false);
      onSignatureChange(null);
    } else {
      setTypedSignature('');
      onSignatureChange(null);
    }
  };

  const handleModeChange = (newMode: 'typed' | 'drawn') => {
    if (newMode !== mode) {
      setMode(newMode);
      // Clear the other mode's signature when switching
      if (newMode === 'typed') {
        signaturePadRef.current?.clear();
        setHasDrawnSignature(false);
        if (typedSignature.trim()) {
          onSignatureChange({ type: 'typed', data: typedSignature });
        } else {
          onSignatureChange(null);
        }
      } else {
        // Going to drawn mode, clear typed signature effect
        onSignatureChange(null);
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* Mode Toggle */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => handleModeChange('drawn')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            mode === 'drawn'
              ? 'bg-telvia-600 text-white'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          Draw Signature
        </button>
        <button
          type="button"
          onClick={() => handleModeChange('typed')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            mode === 'typed'
              ? 'bg-telvia-600 text-white'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          Type Signature
        </button>
      </div>

      {/* Signature Input */}
      {mode === 'typed' ? (
        <div>
          <input
            type="text"
            value={typedSignature}
            onChange={(e) => handleTypedChange(e.target.value)}
            placeholder="Type your full legal name"
            className="w-full px-4 py-3 border border-slate-300 rounded-lg text-xl focus:outline-none focus:border-telvia-500 focus:ring-1 focus:ring-telvia-500"
            style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive", fontStyle: 'italic' }}
          />
          {typedSignature && (
            <div className="mt-3 p-4 bg-slate-50 border border-slate-200 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Preview:</p>
              <p
                className="text-2xl"
                style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive", fontStyle: 'italic' }}
              >
                {typedSignature}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className="border-2 border-slate-300 rounded-lg bg-white overflow-hidden">
            <canvas
              ref={canvasRef}
              className="w-full h-32 cursor-crosshair touch-none"
              style={{ touchAction: 'none' }}
            />
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Use your mouse or finger to draw your signature
          </p>
        </div>
      )}

      {/* Status and Clear */}
      <div className="flex justify-between items-center">
        <button
          type="button"
          onClick={clearSignature}
          className="text-sm text-slate-500 hover:text-slate-700"
        >
          Clear Signature
        </button>
        <div className="text-sm">
          {mode === 'typed' && typedSignature.trim() && (
            <span className="text-green-600 flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              Signature entered
            </span>
          )}
          {mode === 'drawn' && hasDrawnSignature && (
            <span className="text-green-600 flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              Signature captured
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
